import { Autocomplete } from '@mui/material';
import { FC, ReactNode } from 'react';
import {
  Control,
  Controller,
  FieldError,
  FieldErrorsImpl,
  FieldValues,
  Merge,
} from 'react-hook-form';

import { AUTOCOMPLETE_Z_INDEX } from 'style/constants';
import FormTextField from './FormTextField/FormTextField';

export interface IOption<T = string | number> {
  value: T;
  label: string;
}

interface IFieldValues {
  [key: string]: IOption | null;
}

export interface IFormAutocomplete {
  id: string;
  name: string;
  placeholder?: string;
  variant?: 'standard';
  options: IOption[];
  defaultValue?: unknown;
  required?: boolean;
  disabled?: boolean;
  error?: string | FieldError | Merge<FieldError, FieldErrorsImpl<any>>;
  control: Control<FieldValues>;
  label?: ReactNode;
  sx?: object;
  readOnly?: boolean;
}

const FormAutocomplete: FC<IFormAutocomplete> = ({
  id,
  name,
  options,
  control,
  error,
  label,
  required,
  disabled,
  readOnly = false,
  ...rest
}) => (
  <Controller<IFieldValues>
    control={control}
    name={name}
    render={({ field }) => (
      <Autocomplete
        id={id}
        onChange={(_event, option) => {
          field.onChange(option);
        }}
        value={field.value || null}
        isOptionEqualToValue={(option, value) => option.value === value.value}
        onBlur={field.onBlur}
        disabled={disabled}
        options={options}
        slotProps={{ popper: { sx: { zIndex: AUTOCOMPLETE_Z_INDEX } } }}
        renderOption={(props, option: IOption) => (
          <li {...props} key={option.value.toString()}>
            {option.label}
          </li>
        )}
        popupIcon={readOnly ? null : undefined}
        readOnly={readOnly}
        getOptionLabel={(option: IOption) => (option.label ? option.label : '')}
        renderInput={(params) => (
          <FormTextField
            {...params}
            label={label}
            error={!!error}
            helperText={<>{error ?? null}</>}
            required={!!required}
            disabled={disabled}
            {...rest}
          />
        )}
      />
    )}
  />
);

export default FormAutocomplete;
